"use client";

import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const switchVariants = cva("", {
	variants: {
		size: {
			default: "h-6 w-12",
			sm: "h-5 w-9",
		},
	},
	defaultVariants: { size: "default" },
});

const switchThumbVariants = cva("", {
	variants: {
		size: {
			default: "h-[18px] w-[18px] data-[state=checked]:translate-x-6",
			sm: "h-[14px] w-[14px] data-[state=checked]:translate-x-4",
		},
	},
	defaultVariants: { size: "default" },
});

/** Styled toggle built on Radix `SwitchPrimitive.Root`. Forwards all primitive props and ref. */
const Switch = React.forwardRef<
	React.ElementRef<typeof SwitchPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> &
		VariantProps<typeof switchVariants>
>(({ className, size, ...props }, ref) => (
	<SwitchPrimitive.Root
		ref={ref}
		data-slot="switch"
		className={cn(
			"peer inline-flex shrink-0 items-center rounded-full border border-transparent p-[2px] shadow-xs outline-none transition-all",
			switchVariants({ size }),
			"data-[state=checked]:bg-success data-[state=unchecked]:bg-switch-off",
			"focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2",
			"disabled:cursor-not-allowed disabled:opacity-50",
			className,
		)}
		{...props}
	>
		<SwitchPrimitive.Thumb
			data-slot="switch-thumb"
			className={cn(
				"pointer-events-none block rounded-full bg-background data-[state=unchecked]:bg-switch-thumb-off ring-0 transition-transform data-[state=unchecked]:translate-x-0",
				switchThumbVariants({ size }),
			)}
		/>
	</SwitchPrimitive.Root>
));
Switch.displayName = SwitchPrimitive.Root.displayName;

export { Switch };
